import { BaseApiClient, API_BASE_URL } from './base-client';
import type { ApiResponse } from '../../types';
import { clearAuthData, getAuthHeaders } from '../../utils/authHeaders';

/**
 * Audio API - методы для работы с аудио (TTS, STT)
 */
export class AudioApi extends BaseApiClient {
  
  // ===== AUDIO API (Speech-to-Text & Text-to-Speech) =====
  
  /**
   * Transcribe audio to text using OpenAI Whisper or GPT-4o-transcribe
   * Документация: https://platform.openai.com/docs/guides/speech-to-text
   */
  async transcribeAudio(params: {
    audioFile: File;
    model?: 'gpt-4o-transcribe' | 'gpt-4o-mini-transcribe' | 'whisper-1';
    response_format?: 'json' | 'text' | 'srt' | 'verbose_json' | 'vtt';
    prompt?: string; // Подсказка для улучшения качества
    language?: string; // ISO 639-1 код языка
  }): Promise<ApiResponse<{ text: string }>> {
    const formData = new FormData();
    formData.append('file', params.audioFile);
    formData.append('model', params.model || 'gpt-4o-transcribe');
    
    if (params.response_format) {
      formData.append('response_format', params.response_format);
    }
    
    if (params.prompt) {
      formData.append('prompt', params.prompt);
    }
    
    if (params.language) {
      formData.append('language', params.language);
    }

    const response = await this.client.post('/api/audio/transcriptions', formData, {
      // Content-Type удаляется в interceptor'е для FormData
      // Axios автоматически установит multipart/form-data с boundary
    });
    
    return response.data;
  }

  /**
   * Generate speech from text using OpenAI TTS
   * Возвращает URL аудио файла (S3) или Blob URL (fallback)
   * Документация: https://platform.openai.com/docs/guides/text-to-speech
   */
  async textToSpeech(params: {
    text: string;
    model?: 'gpt-4o-mini-tts' | 'tts-1' | 'tts-1-hd';
    voice?: 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'fable' | 'nova' | 'onyx' | 'sage' | 'shimmer';
    instructions?: string; // Для gpt-4o-mini-tts (тон, акцент и т.д.)
    response_format?: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm';
    speed?: number; // 0.25 - 4.0
  }): Promise<{
    audioUrl: string;
    cached?: boolean;
    cacheId?: string;
    isBlob?: boolean; // true если это blob URL (S3 не включен)
  }> {
    const payload = {
      text: params.text,
      model: params.model || 'gpt-4o-mini-tts',
      voice: params.voice || 'coral',
      ...(params.instructions && { instructions: params.instructions }),
      ...(params.response_format && { response_format: params.response_format }),
      ...(params.speed && { speed: params.speed })
    };

    try {
      console.log('📤 Sending TTS request with payload:', payload);
      
      // Сервер возвращает аудио как blob (без S3)
      const response = await this.client.post('/api/audio/speech', 
        payload,
        { responseType: 'blob' }
      );
      
      console.log('📨 Blob response received:', {
        status: response.status,
        size: response.data?.size
      });
    
      // Создаем blob URL
      const audioBlob = response.data;
      const blobUrl = URL.createObjectURL(audioBlob);
      
      console.log('🎵 TTS Blob created:', {
        size: audioBlob.size,
        type: audioBlob.type,
        url: blobUrl.substring(0, 50) + '...'
      });
      
      return {
        audioUrl: blobUrl,
        isBlob: true
      };
      
    } catch (error: any) {
      console.error('❌ TTS request failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  /**
   * Stream audio transcription in real-time
   * Только для gpt-4o-transcribe и gpt-4o-mini-transcribe
   */
  async streamTranscription(params: {
    audioFile: File;
    model: 'gpt-4o-transcribe' | 'gpt-4o-mini-transcribe';
    onChunk: (text: string) => void;
  }): Promise<void> {
    const formData = new FormData();
    formData.append('file', params.audioFile);
    formData.append('model', params.model);
    formData.append('stream', 'true');

    // Подготавливаем заголовки с авторизацией и платформой
    const authHeaders = getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/audio/transcriptions`, {
      method: 'POST',
      headers: authHeaders,
      body: formData,
    });

    if (!response.ok) {
      // Обработка 401 - данные авторизации устарели
      if (response.status === 401) {
        // Проверяем, запущено ли приложение внутри Telegram Mini App
        const webAppData = (window as any).Telegram?.WebApp?.initData;
        const isInMiniApp = typeof webAppData === 'string' && webAppData.length > 0;
        
        if (isInMiniApp) {
          // В Mini App НЕ редиректим на login - OAuth widget не работает в iframe
          console.error('❌ Auth error in Mini App (401)');
          const webApp = (window as any).Telegram?.WebApp;
          if (webApp?.showPopup) {
            webApp.showPopup({
              title: 'Ошибка авторизации',
              message: 'Пожалуйста, закройте и откройте приложение заново.',
              buttons: [{ type: 'close' }]
            });
          }
        } else {
          clearAuthData();
          localStorage.removeItem('user-storage');
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      throw new Error('Transcription failed');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Streaming not supported');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const event = JSON.parse(data);
              if (event.type === 'transcript.text.delta' && event.delta) {
                params.onChunk(event.delta);
              }
            } catch (e) {
              // Игнорируем некорректные JSON события
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

